import { Airline } from "@/types/flights/airline"
import { FlightLegTicketing } from "@/types/flights/flight-itinerary"
import { FlightLegApi } from "@/types/flights/flight-leg"
import { FlightSliceLayover } from "@/types/flights/flight-slice-layover"
import uniqBy from "lodash/uniqBy"

import { getDayObject, nightsBetweenDayObjects } from "@/lib/date-utils"
import { arrivalWallClock, departureWallClock } from "@/lib/flight-utils"
import { Locale } from "@/lib/locale-hooks"
import IconAlert from "@/components/icons/alert"
import IconCircle from "@/components/icons/circle"
import IconInfo from "@/components/icons/info"
import IconLocationPin from "@/components/icons/location-pin"
import IconPlane from "@/components/icons/plane"
import { Separator } from "./separator"

function overnightIndicator() {
  return (
    <>
      <span>·</span>
      <span className="text-red-10">Overnight</span>
      <IconAlert className="text-red-10" />
    </>
  )
}

export function FlightLegsDetail({
  legs,
  layovers,
  leg_ticketing,
}: {
  legs: FlightLegApi[]
  layovers: FlightSliceLayover[]
  leg_ticketing?: FlightLegTicketing[]
}) {
  const formatFlightDuration = Locale.useFormatFlightDuration()
  const formatHourMinute = Locale.useFormatHourMinute()
  const formatWeekdayMonthDay = Locale.useFormatWeekdayMonthDay()

  const initialDeparture = departureWallClock(legs[0])
  const initialDepartureDay = getDayObject(
    initialDeparture.at,
    initialDeparture.timeZone
  )

  const airlines = uniqBy(
    legs
      .map((leg) =>
        leg.properties.airlines.find((a) => a.iata === leg.airline_iata)
      )
      .filter((a) => a != null) as Airline[],
    "iata"
  )

  return (
    <div className="text-xs">
      {legs.map((leg, lix) => {
        const legCabinClassName = leg_ticketing?.[lix].cabin_class_name
        const layover = lix === 0 ? null : layovers[lix - 1]

        // sometimes the airline_iata is not in the list of airlines
        const legAirline =
          airlines.find((a) => a.iata === leg.airline_iata) ??
          ({
            name: leg.airline_iata,
            iata: leg.airline_iata,
          } satisfies Airline)

        const operatingAirline = leg.properties.airlines.find(
          (a) => a.iata === leg.operating_airline_iata
        )
        const operatingAirlineName =
          leg.airline_iata !== leg.operating_airline_iata
            ? operatingAirline?.name || leg.operating_airline_iata
            : null

        const legOrigin = leg.departure_airport
        const legDestination = leg.arrival_airport

        const departure = departureWallClock(leg)
        const departDay = getDayObject(departure.at, departure.timeZone)
        const departDayExp = nightsBetweenDayObjects(
          initialDepartureDay,
          departDay
        )
        const arrival = arrivalWallClock(leg)
        const arriveDay = getDayObject(arrival.at, arrival.timeZone)
        const arriveDayExp = nightsBetweenDayObjects(
          initialDepartureDay,
          arriveDay
        )
        const prevArrival = lix > 0 ? arrivalWallClock(legs[lix - 1]) : null
        const prevArriveDay = prevArrival
          ? getDayObject(prevArrival.at, prevArrival.timeZone)
          : null

        const overnight = nightsBetweenDayObjects(departDay, arriveDay) > 1
        const layoverOvernight = prevArriveDay
          ? nightsBetweenDayObjects(prevArriveDay, departDay) > 1
          : false

        const legNotes = leg.properties.notes?.filter(
          (note) => note.type !== "connection" && note.type !== "redeye"
        )

        return (
          <div key={`leg-${lix}`} className="@container/leg">
            {layover && (
              <>
                <Separator className="mt-3 mb-4" />
                <div className="flex items-center justify-center gap-1.5 text-muted @md/leg:ml-[calc(theme(width.24)+theme(space.4)*2+18px)] @md/leg:justify-start">
                  <span className="no-translate">
                    {formatFlightDuration(layover.duration)}
                  </span>{" "}
                  <span className="no-translate">
                    {layover.airport.name} ({layover.airport.code})
                  </span>
                  {layoverOvernight && overnightIndicator()}
                </div>
                <Separator className="mt-4 mb-3" />
              </>
            )}
            <div className="flex">
              <div className="flex flex-1 gap-2 @md/leg:gap-4">
                <div className="flex w-16 flex-col text-right font-semibold @md/leg:w-24">
                  <span className="no-translate relative">
                    {formatHourMinute(departure.at, departure.timeZone)}
                    {departDayExp > 0 && (
                      <sup className="mr-[-1em]">+{departDayExp}</sup>
                    )}
                  </span>
                  <span className="no-translate relative mt-auto">
                    {formatHourMinute(arrival.at, arrival.timeZone)}
                    {arriveDayExp > 0 && (
                      <sup className="mr-[-1em]">+{arriveDayExp}</sup>
                    )}
                  </span>
                  {arriveDayExp > 0 && <span>&nbsp;</span>}
                </div>
                <div className="-mt-1 ml-0.5 flex w-[18px] flex-col items-center">
                  {lix === 0 && <IconPlane className="text-[2em]" />}
                  {lix !== 0 && (
                    <IconCircle
                      className="mt-px -mb-1 text-[1.75em]"
                      strokeWidth={1.6}
                    />
                  )}
                  <div className="my-2 w-0.5 flex-1 self-center bg-separator" />
                  {lix !== legs.length - 1 && (
                    <IconCircle
                      className="-mt-1 -mb-0.5 text-[1.75em]"
                      strokeWidth={1.6}
                    />
                  )}
                  {lix === legs.length - 1 && (
                    <IconLocationPin className="-mb-1 text-[1.875em]" />
                  )}
                  {arriveDayExp > 0 && <span>&nbsp;</span>}
                </div>
                <div className="flex flex-1 flex-col">
                  <div>
                    <div className="no-translate font-semibold">
                      {legOrigin.name} ({legOrigin.code})
                    </div>
                    <div className="font-normal text-muted">
                      <span className="no-translate">{legAirline.name}</span>
                      {operatingAirlineName && (
                        <span>
                          {" "}
                          (Operated by{" "}
                          <span className="no-translate">
                            {operatingAirlineName}
                          </span>
                          )
                        </span>
                      )}
                      {legCabinClassName && (
                        <>
                          <span className="mx-1">·</span>
                          <span className="capitalize">
                            {legCabinClassName}
                          </span>
                        </>
                      )}
                      <span className="mx-1">·</span>
                      <span className="no-translate">
                        {legAirline.iata} {leg.flight_number}
                      </span>
                      {leg.properties.aircraft && (
                        <>
                          <span className="mx-1">·</span>
                          <span className="no-translate">
                            {leg.properties.aircraft}
                          </span>

                          {leg.properties.aircraft_type && (
                            <span>
                              {" "}
                              -{" "}
                              <span className="no-translate">
                                {leg.properties.aircraft_type}
                              </span>
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    {departDayExp > 0 && (
                      <div className="text-red-10">
                        Departs{" "}
                        <span className="no-translate">
                          {formatWeekdayMonthDay(
                            departure.at,
                            departure.timeZone
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="my-auto flex items-center gap-1.5 py-4">
                    <span className="no-translate">
                      {formatFlightDuration(leg.duration)}
                    </span>
                    {overnight && overnightIndicator()}
                  </div>
                  <div className="font-semibold">
                    <span className="no-translate">
                      {legDestination.name} ({legDestination.code})
                    </span>
                    {arriveDayExp > 0 && (
                      <div className="font-normal text-red-10">
                        Arrives{" "}
                        <span className="no-translate">
                          {formatWeekdayMonthDay(arrival.at, arrival.timeZone)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Todo show this again when we get real data
                    <div className="invisible flex-col gap-2 px-4 text-muted @md/leg:flex">
                      <div className="flex items-center gap-1.5">
                        <IconLegroom className="text-[1.5em]" />
                        <span>Average Legroom (31 in) [FAKE]</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <IconWifi className="text-[1.5em]" />
                        <span>Wi-Fi for a fee [FAKE]</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <IconPlug className="text-[1.5em]" />
                        <span>In-seat power outlet (USB) [FAKE]</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <IconTv className="text-[1.5em]" />
                        <span>Live TV [FAKE]</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <IconCo2 className="text-[1.5em]" />
                        <span>Carbon emissions: 365kg [FAKE]</span>
                      </div>
                    </div>
                  */}
            </div>

            {legNotes && legNotes.length > 0 && (
              <div className="mx-5 mt-6 flex flex-row rounded-sm border border-yellow-6 bg-yellow-2 p-2">
                <div className="mr-4 flex items-center font-semibold">
                  <IconInfo className="text-[1.5em] text-yellow-8" />
                </div>

                <div>
                  <ul className="list-inside list-disc">
                    {legNotes.map((note, ix) => {
                      return <li key={`note-${ix}`}>{note.value}</li>
                    })}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
